import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { clsx } from 'clsx'
import { useWatchableRef } from 'lib/hooks'

const useFileDrop = (watchableRef, { onFilesDrop }) => {
  const [draggingOver, setDraggingOver] = useState(false)

  const dragEnter = useCallback(
    (e) => {
      if (watchableRef.current?.contains(e.target)) {
        setDraggingOver(true)
        e.stopPropagation()
        e.preventDefault()
      }
    },
    [setDraggingOver, watchableRef.version]
  )

  const dragLeave = useCallback(
    (e) => {
      if (watchableRef.current?.contains(e.target)) {
        setDraggingOver(false)
        e.stopPropagation()
        e.preventDefault()
      }
    },
    [setDraggingOver, watchableRef.version]
  )

  const dragOver = useCallback(
    (e) => {
      if (watchableRef.current?.contains(e.target)) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [setDraggingOver, watchableRef.version]
  )

  const drop = useCallback(
    (e) => {
      if (watchableRef.current?.contains(e.target)) {
        onFilesDrop(e)
        setDraggingOver(false)
        e.stopPropagation()
        e.preventDefault()
      }
    },
    [setDraggingOver, watchableRef.version, onFilesDrop]
  )

  useEffect(() => {
    window.addEventListener('dragenter', dragEnter)
    window.addEventListener('dragleave', dragLeave)
    window.addEventListener('drop', drop)
    window.addEventListener('dragover', dragEnter)

    return () => {
      window.removeEventListener('dragenter', dragEnter)
      window.removeEventListener('dragleave', dragLeave)
      window.removeEventListener('drop', drop)
      window.removeEventListener('dragover', dragOver)
    }
  }, [dragEnter, dragLeave, drop, dragOver])

  return useMemo(
    () => ({
      draggingOver,
    }),
    [draggingOver]
  )
}

export function Header({ children, multiple, onFilesAdded, accepts }) {
  const hiddenInputRef = useRef(null)
  const onClick = (e) => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.click()
    }
  }

  const onAddFile = (e) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files)
      onFilesAdded(selectedFiles)
      hiddenInputRef.current.value = ''
    }
  }

  const onFilesDrop = useCallback(
    (e) => {
      if (e.dataTransfer.files) {
        const selectedFiles = Array.from(e.dataTransfer.files)
        if (!multiple) {
          selectedFiles.splice(1)
        }
        onFilesAdded(selectedFiles)
      }
    },
    [multiple, onFilesAdded]
  )
  const headerRef = useWatchableRef()
  const { draggingOver } = useFileDrop(headerRef, { onFilesDrop })

  return (
    <div
      className={clsx('file-upload-header dome-full-width dome-rounded-border', { 'drop': draggingOver })}
      onClick={onClick}
      ref={headerRef.ref}
    >
      <input
        type="file"
        ref={hiddenInputRef}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={onAddFile}
        accept={accepts?.length > 0 ? accepts.join(',') : undefined}
      />
      {children}
    </div>
  )
}

Header.propTypes = {
  children: PropTypes.node.isRequired,
  multiple: PropTypes.bool.isRequired,
  onFilesAdded: PropTypes.func.isRequired,
  accepts: PropTypes.arrayOf(PropTypes.string),
}
